import React, {
  Fragment, useEffect, useMemo, useRef, useState,
} from 'react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import _ from 'lodash';
import { toast } from 'react-toastify';
import { RangeDatePicker } from '../../../_common/Form/DatePickers/RangeDatePicker/RangeDatePicker';
import { useQuery } from '../../../../utils/hooks/useQuery';
import Button from '../../../_common/Form/Buttons/Button';
import classes from './repBonus.module.scss';
import Loader from '../../../_common/Loader/Loader';
import Utils from '../../../../utils/utils';
import Api from '../../../../api/api';
import Select from '../../../_common/Form/Selects/Select/Select';
import StatusField from '../../../_common/StatusField/StatusField';
import Modal from '../../../_common/Modal/Modal';
import InputField from '../../../_common/Form/TextFields/InputField';
import AccountHelpers from '../../../../utils/accountHelpers';
import RepBonusModal from './RepBonusModal';
import AttentionModal from '../../../_common/Modal/AttentionModal/AttentionModal';

const repInfo = [
  {
    title: 'Total prescription count',
    path: 'sales',
  },
  {
    title: 'Total sales',
    path: 'revenue',
    type: 'currency',
  },
  {
    title: 'Total bonus',
    path: 'totalBonus',
    type: 'currency',
  },
];

const infoFields = [
  {
    title: 'Total prescription count',
    subInfo: [
      {
        title: 'Total',
        path: 'sales',
      },
      {
        title: 'Specialty count',
        path: 'specialty',
        className: classes.sub,
        query: {
          specialty: 'true',
        },
      },
      {
        title: 'Retail count',
        path: 'retail',
        className: classes.sub,
        query: {
          specialty: 'false',
        },
      },
    ],
  },
  {
    title: 'Total bonus',
    subInfo: [
      {
        title: 'Total',
        path: 'totalBonus',
        leftSymbol: '$',
        type: 'currency',
      },
      {
        title: 'Specialty bonus',
        path: 'bonusSpecialty',
        className: classes.sub,
        leftSymbol: '$',
        type: 'currency',
        query: {
          specialty: 'true',
        },
      },
      {
        title: 'Retail bonus',
        path: 'bonusRetail',
        className: classes.sub,
        leftSymbol: '$',
        type: 'currency',
        query: {
          specialty: 'false',
        },
      },
      {
        title: 'Incentive bonus',
        path: 'incentivesBonus',
        className: classes.sub,
        leftSymbol: '$',
        type: 'currency',
        query: {
          incentives: 'true',
        },
        subData: {
          childPath: 'incentivesBonusData',
          paths: [{
            title: 'Product name',
            path: 'name',
            type: 'string',
          },
          {
            title: 'Script count',
            path: 'scriptCount',
          },
          {
            title: 'Quantity',
            path: 'quantity',
          },
          {
            title: 'Total',
            path: 'total',
            leftSymbol: '$',
            type: 'currency',
          }],
        },
      },
    ],
  },
  {
    title: 'Financials',
    subInfo: [
      {
        title: 'Total sales',
        path: 'revenue',
        leftSymbol: '$',
        type: 'currency',
      },
      {
        title: 'Incentive’s products',
        path: 'incentivesProducts',
        leftSymbol: '$',
        className: classes.sub,
        type: 'currency',
        query: {
          incentives: 'true',
        },
      },
      {
        title: 'Other products',
        path: 'forSpecialty',
        leftSymbol: '$',
        className: classes.sub,
        type: 'currency',
        query: {
          specialty: 'true',
        },
      },
      {
        title: 'Profit',
        path: 'profit',
        leftSymbol: '$',
        type: 'currency',
      },
      {
        title: 'Margin',
        path: 'margin',
        rightSymbol: '%',
      },
    ],
  },
  {
    title: 'Engagement',
    subInfo: [
      {
        title: 'New physicians',
        path: 'newPhysicians',
      },
      {
        title: 'New patients',
        path: 'newPatients',
        query: {
          refills: '0',
        },
      },
      {
        title: 'Appointments',
        path: 'appointment',
      },
    ],
  },
];

const collectRequestData = (date) => {
  const data = Utils.deleteEmptyKeys(date);

  if (data.start) {
    if (!data.end) {
      data.end = moment(data.start).format('YYYY-MM-DD');
    } else {
      data.end = moment(data.end).format('YYYY-MM-DD');
    }
  }

  return data;
};

const thisMonthStartDay = moment().startOf('month').format('YYYY-MM-DD');
const thisMonthEndDate = moment().format('YYYY-MM-DD');

const RepBonus = ({ type = 'bonus' }) => {
  const navigate = useNavigate();
  const { query, queryToStringify, setQuery } = useQuery();

  const firstLoad = useRef(true);
  const repListsWrapRef = useRef(null);
  const repWrapRef = useRef(null);

  const [date, setDate] = useState({
    start: query.start || thisMonthStartDay,
    end: query.start && !query.end ? '' : query.end || thisMonthEndDate,
  });

  const [repsData, setRepsData] = useState({
    list: [],
    totalPages: 1,
    page: 1,
  });

  const { list } = repsData;

  const [rep, setRep] = useState({});

  const [status, setStatus] = useState('Unpaid');

  const [payModalInfo, setPayModalInfo] = useState({});
  const [payModalAmountError, setPayModalAmountError] = useState(false);

  const [deleteModalInfo, setDeleteModalInfo] = useState({});

  const [loading, loadingToggle] = useState(true);

  const [paymentLoading, paymentLoadingToggle] = useState(false);
  const [savePaymentLoading, savePaymentLoadingToggle] = useState(false);

  const [scrollLoading, scrollLoadingToggle] = useState(false);

  const getRepPayment = async (repData) => {
    setStatus('Unpaid');

    if (type === 'bonus') {
      const { data: { data: payments } } = await Api.getDashboardRepPayment({
        start: moment(date.start).format('YYYY-MM'),
        end: moment(date.end || date.start).add(1, 'month').format('YYYY-MM'),
        name: repData.name,
      });

      setRep({ ...repData, payments });
    } else {
      setRep(repData);
    }
  };

  const getRepBonus = async (searchParams = date) => {
    const sendingData = collectRequestData(searchParams);

    const { data } = type === 'schedule'
      ? await Api.getDashboardRepSchedule(sendingData)
      : await Api.getDashboardRepScorecard(sendingData);

    const formattedLists = data.data.map((d) => ({ ...d, incentivesProducts: +d.revenue - +d.forSpecialty }));

    if (!formattedLists.length) {
      setRep({});
    } else if (!searchParams.page || +searchParams?.page < 2) {
      await getRepPayment(formattedLists[0]);
    }

    setRepsData((prev) => ({
      ...prev,
      list: searchParams.page > 1 ? [...prev.list, ...formattedLists] : formattedLists,
      totalPages: data._meta.pageCount,
      page: data._meta.currentPage,
    }));
  };

  useEffect(() => {
    (async () => {
      loadingToggle(true);
      setQuery(Utils.deleteEmptyKeys({ ...date }));

      await getRepBonus();

      if (repListsWrapRef.current) repListsWrapRef.current.scrollTop = 0;
      if (repWrapRef.current) repWrapRef.current.scrollTop = 0;

      firstLoad.current = false;
      loadingToggle(false);
    })();
  }, [date]);

  const selectRep = async (currentRep) => {
    paymentLoadingToggle(true);
    setRep(currentRep);

    repWrapRef.current.scrollTop = 0;

    await getRepPayment(currentRep);

    paymentLoadingToggle(false);
  };

  const onChangeDate = async (currentDate) => {
    setDate((prev) => ({ ...prev, start: currentDate[0], end: currentDate[1] }));
  };

  const clearFilters = () => {
    setDate({
      start: thisMonthStartDay,
      end: thisMonthEndDate,
    });
  };

  const getRepsByScrolling = async (e) => {
    if (!loading && !scrollLoading
      && e.target.scrollTop + e.target.getBoundingClientRect().height + 10 >= e.target.scrollHeight) {
      if (+repsData.totalPages >= +repsData.page + 1) {
        scrollLoadingToggle(true);

        await getRepBonus({ page: +repsData.page + 1, ...date });

        scrollLoadingToggle(false);
      }
    }
  };

  const changeModalData = (path, value) => {
    setPayModalInfo((prev) => {
      const newData = { ...prev, [path]: value };

      if (path === 'paymentMethod') {
        newData.checkNumber = '';
      }

      return newData;
    });

    if (path === 'amount') setPayModalAmountError(false);
  };

  const pay = async (e) => {
    e.preventDefault();

    if (!payModalInfo.amount?.trim()) {
      setPayModalAmountError(true);

      return toast.error('Please fill Amount field');
    }

    try {
      savePaymentLoadingToggle(true);

      const { data: { data: newPayment } } = await Api.payBonus(payModalInfo);

      setRep((prev) => {
        const year = prev.payments[payModalInfo.year];
        const month = prev.payments[payModalInfo.year].months[payModalInfo.month];
        const remainder = +(month.remainder - +payModalInfo.amount).toFixed(2);

        return {
          ...prev,
          payments: {
            ...prev.payments,
            [payModalInfo.year]: {
              ...year,
              months: {
                ...year.months,
                [payModalInfo.month]: {
                  ...month,
                  remainder,
                  status: remainder <= 0 ? 'Paid' : 'Partial',
                  payments: [...(month.payments || []), newPayment],
                },
              },
            },
          },
        };
      });
      setPayModalInfo({});
    } catch (err) {
      toast.error('Something went wrong');
      console.warn(err);
    }

    savePaymentLoadingToggle(false);
  };

  const cancelPayment = async () => {
    try {
      savePaymentLoadingToggle(true);

      await Api.deleteInvoice(deleteModalInfo);

      const filteredPayments = payModalInfo.payments.filter((p) => p.id !== deleteModalInfo.id);

      setRep((prev) => {
        const year = moment(payModalInfo.date).format('YYYY');
        const month = moment(payModalInfo.date).format('MMMM');

        const currentYearData = prev.payments[year];
        const currentMonthData = prev.payments[year].months[month];
        const remainder = +(currentMonthData.remainder + +deleteModalInfo.amount).toFixed(2);

        return {
          ...prev,
          payments: {
            ...prev.payments,
            [year]: {
              ...currentYearData,
              months: {
                ...currentYearData.months,
                [month]: {
                  ...currentMonthData,
                  remainder,
                  status: !filteredPayments.length ? 'Unpaid' : 'Partial',
                  payments: filteredPayments,
                },
              },
            },
          },
        };
      });

      if (!filteredPayments.length) {
        setPayModalInfo({});
      } else {
        setPayModalInfo((prev) => ({
          ...prev, payments: filteredPayments,
        }));
      }
      setDeleteModalInfo({});
    } catch (err) {
      toast.error('Something went wrong');
      console.warn(err);
    }

    savePaymentLoadingToggle(false);
  };

  const closeModal = () => {
    if (!savePaymentLoading) setPayModalInfo({});
  };

  const singleRepPageClick = (searchParams = {}) => {
    const queryRequest = {
      start: query.start,
      end: query.end || query.start,
      name: rep.name,
      ...searchParams,
      report: 'Rep',
    };

    if (type === 'schedule') {
      queryRequest.status = type;
    }

    navigate(`/dashboard/report-single${queryToStringify(queryRequest)}`);
  };

  const { header, period } = useMemo(() => {
    const h = (
      <>
        {type === 'schedule' ? 'Number of prescription RX Total pay of not shipped of the' : 'Consolidated income statement of the'}
        {' '}
        <strong>{rep.name}</strong>
      </>
    );

    const p = date.end
      ? `period from ${moment(date.start).format('MM/DD/YYYY')} to ${moment(date.end).format('MM/DD/YYYY')}`
      : `period for the ${moment(date.start).format('MM/DD/YYYY')}`;

    return { header: h, period: p };
  }, [rep.name, date]);

  const formattedPayments = useMemo(() => _.map(rep.payments, (value, key) => ({
    year: key,
    total: value.total,
    months: _.orderBy(
      _.map(value.months, (v, k) => ({
        month: k, total: v.total, status: v.status, remainder: v.remainder.toString(), payments: v.payments,
      })),
      (m) => moment(m.month, 'MMMM'),
    )
      .filter((m) => m.status.includes(status)).reverse(),
  })).filter((p) => p.months.length), [rep.payments, status]);

  return (
    <>
      {type === 'bonus' && (
        <Button
          href={{ pathname: '/dashboard/invoices', search: queryToStringify({ start: query.start, end: query.end }) }}
          title="Invoice History"
          reverseColor
          className={classes.history_btn}
        />
      )}

      <div className={classes.wrapper}>
        <div className={classes.content}>
          {loading && <Loader className={classes.loader} />}

          {!firstLoad.current && !list.length && <div className={classes.no_result}>No Data Found</div>}

          <div className={classes.left_block}>
            <div className={classes.filters_wrapper}>
              <RangeDatePicker
                currentDate={date}
                onChangeDate={onChangeDate}
                showLastYear
              />

              <div className={classes.button_wrapper}>
                <Button
                  title="Clear"
                  clearButton
                  onClick={clearFilters}
                />
              </div>
            </div>

            <div className={classes.reps_wrapper} ref={repListsWrapRef} onScroll={getRepsByScrolling}>
              {list.map((d) => (
                <div
                  key={d.name}
                  className={`${classes.rep} ${rep.name === d.name ? classes.active : ''}`}
                  onClick={() => selectRep(d)}
                >
                  <div className={classes.name}>{d.name}</div>

                  <div className={classes.rep_info}>
                    {repInfo.map((info) => (
                      <div key={info.path}>
                        <p>{info.title}</p>

                        <p>{info.type === 'currency' ? Utils.formattingCurrency(d[info.path]) : d[info.path]}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {scrollLoading && <Loader className={classes.scroll_loader} size={24} />}
            </div>
          </div>

          {rep.name && (
            <div className={classes.right_block}>
              <h2>{header}</h2>

              <h5>{period}</h5>

              <div className={classes.current_rep_info} ref={repWrapRef}>
                {infoFields.map(({ title, subInfo }) => (
                  <div key={title}>
                    <p>{title}</p>

                    {subInfo.map((info) => (
                      <Fragment key={info.path}>
                        <div
                          style={!(+rep?.[info.path]) ? { pointerEvents: 'none' } : {}}
                          className={info.className}
                          onClick={() => +rep?.[info.path] && singleRepPageClick(info.query)}
                        >
                          <p>{info.title}</p>

                          <p>{(info.leftSymbol || '') + (info.type === 'currency' ? Utils.formattingCurrency(rep?.[info.path], 0) : rep?.[info.path]) + (info.rightSymbol || '')}</p>
                        </div>

                        {info.subData && !!rep?.[info.subData.childPath]?.length && (
                          <div className={classes.table_wrapper}>
                            <div className={classes.table__border}>
                              <table className={classes.table}>
                                <thead className={classes.thead}>
                                  <tr className={classes.tr}>
                                    {info.subData.paths.map((h) => (
                                      <th key={h.title}>
                                        {h.title}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>

                                <tbody className={classes.tbody}>
                                  {rep?.[info.subData.childPath].map((d) => (
                                    <tr
                                      key={d.name}
                                      className={classes.tr}
                                      onClick={() => singleRepPageClick({ productId: d.productId })}
                                    >
                                      {info.subData.paths.map((h) => (
                                        <td key={h.path}>
                                          {`${h.leftSymbol || ''}${h.type === 'currency'
                                            ? Utils.formattingCurrency(d[h.path])

                                            : (d[h.path] ?? ' - ')}`}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </Fragment>
                    ))}
                  </div>
                ))}

                {type === 'bonus' && (
                  <div className={classes.bonuses}>
                    <div className={classes.bonuses_header}>
                      <p>Monthly Bonuses</p>

                      <Select
                        label="Status"
                        wrapperClassName={classes.status_select}
                        options={['Unpaid', 'Partial', 'Paid']}
                        height={38}
                        labelDefaultPosition={6}
                        arrowTop={13}
                        closeOnScroll
                        currentOption={status}
                        onChange={setStatus}
                      />
                    </div>

                    <div className={classes.payments_wrapper}>
                      {paymentLoading
                        ? <Loader className={classes.loader} size={28} />

                        : formattedPayments.length
                          ? formattedPayments.map(({ year, total, months }) => (
                            <div key={year} className={classes.payments}>
                              <div className={classes.year}>
                                <p>
                                  {year}
                                  {' '}
                                  <span>{`${months.length} months`}</span>
                                </p>

                                <p>{`$${Utils.formattingCurrency(total)}`}</p>
                              </div>

                              <div className={classes.months}>
                                {months.map((m) => {
                                  const payBtn = m.status !== 'Paid';

                                  return (
                                    <div key={m.month} className={classes.month}>
                                      <div>
                                        <p>{m.month}</p>

                                        <StatusField status={m.status} className={classes.status_field} />
                                      </div>

                                      <div>
                                        <p>{`$${Utils.formattingCurrency(m.total)}`}</p>

                                        <Button
                                          title={payBtn ? 'Pay' : 'View'}
                                          reverseColor={payBtn}
                                          buttonColor={payBtn ? '#547EEE' : '#5F6C8E'}
                                          onClick={() => setPayModalInfo(payBtn
                                            ? {
                                              isOpen: true,
                                              year,
                                              month: m.month,
                                              userId: rep.repId,
                                              paymentMethod: 'Cash',
                                              date: moment(`${year}-${m.month}`).format('YYYY-MM'),
                                              amount: m.remainder.toString(),
                                            }

                                            : {
                                              isOpen: true,
                                              payments: m.payments,
                                              date: moment(`${year}-${m.month}`).format('MMMM YYYY'),
                                            })}
                                        />
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))

                          : <p className={classes.no_bonuses}>No Bonuses</p>}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {type === 'bonus' && (
          <>
            <RepBonusModal
              modalInfo={payModalInfo}
              onClose={closeModal}
              onPay={pay}
              changeData={changeModalData}
              payAmountError={payModalAmountError}
              savePaymentLoading={savePaymentLoading}
              setDeleteInfo={setDeleteModalInfo}
            />

            <AttentionModal
              onClose={() => setDeleteModalInfo({})}
              isOpen={!_.isEmpty(deleteModalInfo)}
              onSave={cancelPayment}
              title="Cancel Invoice?"
              text="Are you sure you want to cancel this invoice?"
              loading={savePaymentLoading}
            />
          </>
        )}
      </div>
    </>
  );
};

export default RepBonus;
